import React, { useState, useEffect } from 'react'
import { api } from '../utils/api'

function Admin() {
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [editingProduct, setEditingProduct] = useState(null)
  const [activeTab, setActiveTab] = useState('products')
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image: '',
    category: '',
    subcategory: '',
    featured: false
  })
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    image: '',
    subcategories: ''
  })
  const [adminKey, setAdminKey] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadProducts()
    loadCategories()
  }, [])

  const loadProducts = async () => {
    try {
      const productsData = await api.getProducts()
      setProducts(productsData)
    } catch (error) {
      console.error('Failed to load products:', error)
    }
  }

  const loadCategories = async () => {
    try {
      const categoriesData = await api.getCategories()
      setCategories(categoriesData)
    } catch (error) {
      console.error('Failed to load categories:', error)
    }
  }

  const handleProductSubmit = async (e) => {
    e.preventDefault()
    if (!adminKey) {
      setMessage('Please enter admin key')
      return
    }

    try {
      setLoading(true)
      
      if (editingProduct) {
        await api.updateProduct(editingProduct.id, formData, adminKey)
        setMessage('Product updated successfully')
      } else {
        await api.createProduct(formData, adminKey)
        setMessage('Product created successfully')
      }
      
      setFormData({ 
        name: '', 
        description: '', 
        price: '', 
        image: '', 
        category: '', 
        subcategory: '',
        featured: false 
      })
      setEditingProduct(null)
      await loadProducts()
    } catch (error) {
      setMessage(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleCategorySubmit = async (e) => {
    e.preventDefault()
    if (!adminKey) {
      setMessage('Please enter admin key')
      return
    }

    try {
      setLoading(true)
      const subcategories = categoryForm.subcategories
        .split(',')
        .map(s => s.trim())
        .filter(s => s)

      await api.createCategory({
        ...categoryForm,
        subcategories
      }, adminKey)

      setMessage('Category created successfully')
      setCategoryForm({ name: '', description: '', image: '', subcategories: '' })
      await loadCategories()
    } catch (error) {
      setMessage(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (product) => {
    setEditingProduct(product)
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      image: product.image || '',
      category: product.category || '',
      subcategory: product.subcategory || '',
      featured: product.featured || false
    })
    setMessage('')
  }

  const handleDelete = async (id) => {
    if (!adminKey) {
      setMessage('Please enter admin key')
      return
    }

    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        setLoading(true)
        await api.deleteProduct(id, adminKey)
        setMessage('Product deleted successfully')
        await loadProducts()
      } catch (error) {
        setMessage(`Error deleting product: ${error.message}`)
      } finally {
        setLoading(false)
      }
    }
  }

  const clearForm = () => {
    setFormData({ name: '', description: '', price: '', image: '', category: '', subcategory: '', featured: false })
    setEditingProduct(null)
    setMessage('')
  }

  return (
    <div className="container admin-panel">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <div className="admin-tabs">
          <button 
            className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products
          </button>
          <button 
            className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
        </div>
      </div>

      <div className="form-group">
        <label>Admin API Key:</label>
        <input
          type="password"
          value={adminKey}
          onChange={(e) => setAdminKey(e.target.value)}
          placeholder="Enter admin API key"
        />
      </div>

      {message && (
        <div style={{ 
          padding: '1rem', 
          margin: '1rem 0', 
          background: message.includes('Error') ? '#f8d7da' : '#d1edff',
          border: `1px solid ${message.includes('Error') ? '#f5c6cb' : '#bee5eb'}`,
          borderRadius: '4px'
        }}>
          {message}
        </div>
      )}

      {activeTab === 'products' && (
        <>
          <form onSubmit={handleProductSubmit} style={{ marginBottom: '2rem' }}>
            <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Product Name: *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Price: *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description: *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category:</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Subcategory:</label>
                <input
                  type="text"
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Image URL:</label>
              <input
                type="url"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                />
                Featured Product
              </label>
            </div>

            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Processing...' : (editingProduct ? 'Update Product' : 'Add Product')}
            </button>
            
            {editingProduct && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={clearForm}
                style={{ marginLeft: '1rem' }}
              >
                Cancel
              </button>
            )}
          </form>

          <div>
            <h2>Manage Products ({products.length})</h2>
            <div className="products-grid">
              {products.map(product => (
                <div key={product.id} className="product-card">
                  <img
                    src={product.image || '/placeholder-image.jpg'}
                    alt={product.name}
                    className="product-image"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y0ZjRmNCIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                  <div className="product-category">{product.category}</div>
                  <h3>{product.name}</h3>
                  <p className="product-price">${parseFloat(product.price).toFixed(2)}</p>
                  <p>{product.description}</p>
                  {product.featured && <span className="featured-badge">Featured</span>}
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleEdit(product)}
                      style={{ marginRight: '0.5rem' }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDelete(product.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'categories' && (
        <>
          <form onSubmit={handleCategorySubmit} style={{ marginBottom: '2rem' }}>
            <h2>Add New Category</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Category Name: *</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Image URL:</label>
                <input
                  type="url"
                  value={categoryForm.image}
                  onChange={(e) => setCategoryForm({ ...categoryForm, image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                rows="2"
              />
            </div>

            <div className="form-group">
              <label>Subcategories (comma separated):</label>
              <input
                type="text"
                value={categoryForm.subcategories}
                onChange={(e) => setCategoryForm({ ...categoryForm, subcategories: e.target.value })}
                placeholder="Men, Women, Kids, Accessories"
              />
            </div>

            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Processing...' : 'Add Category'}
            </button>
          </form>

          <div>
            <h2>Existing Categories</h2>
            <div className="categories-grid">
              {categories.map(category => (
                <div key={category.id} className="category-card">
                  <h3>{category.name}</h3>
                  <p>{category.description}</p>
                  {category.subcategories.length > 0 && (
                    <div>
                      <strong>Subcategories:</strong>
                      <p>{category.subcategories.join(', ')}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Admin
